import type { FetchQuoteParams } from 'actions/InsuranceActions'
import { createSelector } from 'reselect'
import { getCheckoutInsuranceDates, getCheckoutInsuranceCountries } from './insurance'
import { getCheckoutInsuranceBookingProtectionTravellers, getCheckoutInsuranceTravellers } from './getCheckoutInsuranceTravellers'
import getCheckoutInsuranceCoverAmount from './getCheckoutInsuranceCoverAmount'
import { checkoutHasPreselectedUpstreamInsurance, getInsuranceItemByProductType } from '../view/insurance'
import { isCruiseCheckout } from '../view/cruise'
import { checkoutContainsFlightsWithEmbeddedInsurance, isStandaloneFlights } from '../view/flights'
import { selectInsurancePriceAdjustmentPercentage } from 'lib/insurance/insurancePriceAdjustmentExperiment'

/**
 * Signed percentage this checkout quote asks svc-insurance to apply to the insurance price.
 *
 * Derived from state (not the useExperiment hook) so the identical value can be folded into the
 * quote params below and used by BOTH the fetch hooks and every checkout read-selector — keeping
 * the write and read quote cache keys identical for every experiment arm. Only customers making a
 * fresh insurance decision (no protection pre-selected upstream) get a percentage; everyone else
 * gets `undefined`, leaving their params/key/payload identical to the base price.
 */
export const getCheckoutInsurancePriceAdjustmentPercentage = createSelector(
  (state: App.State) => selectInsurancePriceAdjustmentPercentage(state),
  (state: App.State) => checkoutHasPreselectedUpstreamInsurance(state),
  (percentage, hasPreselectedUpstreamInsurance): number | undefined =>
    hasPreselectedUpstreamInsurance ? undefined : percentage,
)

/**
 * Retrieves the list of parameters to fetch a quote that can be derived
 * from the checkout state, this includes:
 * - Dates
 * - Travellers
 * - Coverage amount
 * - Destination countries
 */
function buildCheckoutInsuranceQuoteParamsSelector(
  getTravellers: (state: App.State) => App.Occupants,
  getInsuranceItem: (state: App.State) => App.Checkout.InsuranceItem | undefined,
  getIsFlightBooking: (state: App.State) => boolean = () => false,
) {
  return createSelector(
    (state: App.State) => getCheckoutInsuranceDates(state),
    (state: App.State) => getTravellers(state),
    (state: App.State) => getCheckoutInsuranceCoverAmount(state),
    (state: App.State) => getCheckoutInsuranceCountries(state),
    (state: App.State) => state.checkout.cart.cartId,
    (state: App.State) => state.checkout.cart.currencyCode,
    (state: App.State) => getInsuranceItem(state),
    (state: App.State) => isCruiseCheckout(state),
    (state: App.State) => getIsFlightBooking(state),
    (state: App.State) => getCheckoutInsurancePriceAdjustmentPercentage(state),
    (dates, travellers, coverAmount, destinationCountries, cartId, currency, item, isCruise, isFlightBooking, priceAdjustmentPercentage): Partial<FetchQuoteParams> => {
      return {
        startDate: dates?.startDate,
        endDate: dates?.endDate,
        travellers,
        coverAmount,
        isCruise,
        cartId,
        currency,
        destinationCountries,
        numberOfSeniors: item?.numberOfSeniors ?? 0,
        isFlightBooking,
        priceAdjustmentPercentage,
      }
    },
  )
}

const getCheckoutInsuranceQuoteParams = buildCheckoutInsuranceQuoteParamsSelector(
  getCheckoutInsuranceTravellers,
  (state: App.State) => getInsuranceItemByProductType(state, 'travel-insurance'),
)

/**
 * A CFMR quote covers a flight booking when the cart is flights-only or its flights already carry
 * embedded protection. svc-insurance uses the flag to price the flight-specific LuxPlus member
 * price and to record the subscription as flight CFMR for later amendments.
 */
const isBookingProtectionFlightBooking = (state: App.State): boolean =>
  checkoutContainsFlightsWithEmbeddedInsurance(state) || isStandaloneFlights(state)

export const getCheckoutInsuranceBookingProtectionQuoteParams = buildCheckoutInsuranceQuoteParamsSelector(
  getCheckoutInsuranceBookingProtectionTravellers,
  (state: App.State) => getInsuranceItemByProductType(state, 'booking-protection'),
  isBookingProtectionFlightBooking,
)

export function getCheckoutInsuranceQuoteParamsForType(
  quoteParams: Partial<FetchQuoteParams>,
  bookingProtectionQuoteParams: Partial<FetchQuoteParams>,
  type: App.InsuranceProductType | undefined,
): Partial<FetchQuoteParams> {
  return type === 'booking-protection' ? bookingProtectionQuoteParams : quoteParams
}

export function getCheckoutInsuranceQuoteParamsByType(
  state: App.State,
  type?: App.InsuranceProductType,
): Partial<FetchQuoteParams> {
  if (type === 'booking-protection') {
    return getCheckoutInsuranceBookingProtectionQuoteParams(state)
  }

  return getCheckoutInsuranceQuoteParams(state)
}

export default getCheckoutInsuranceQuoteParams
